import { Bell, CheckCircle2, MessageSquareText, RefreshCw, Search, UserRoundCheck, WandSparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  loadBot,
  updateAutomationSettings,
  updateBotKeyword,
  updateBotNotice,
  type BotKeyword,
  type BotNotice,
  type BotKeywordAction,
  type TwitchBotSnapshot,
} from './bot-api';
import './workspace.css';

function actionText(action: BotKeywordAction): string {
  if (action.type === 'play_sound') return `Play sound · ${action.value}`;
  return `Random sound · ${action.values.length} choices`;
}

function cleanMessage(value: string): string {
  return value.replace(/legacy/gi, 'previous').replace(/native/gi, 'FFSControl');
}

function validCooldown(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const seconds = Number(value);
  return Number.isInteger(seconds) && seconds >= 0 && seconds <= 3600 ? seconds : null;
}

export function AutomationsPage() {
  const [bot, setBot] = useState<TwitchBotSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [selectedKeyword, setSelectedKeyword] = useState('');
  const [keywordResponse, setKeywordResponse] = useState('');
  const [keywordCooldown, setKeywordCooldown] = useState('0');

  const refresh = useCallback(async () => {
    try {
      const next = await loadBot();
      setBot(next);
      setSelectedKeyword((current) => current && next.automation.keywords.some((item) => item.keyword === current) ? current : next.automation.keywords[0]?.keyword ?? '');
      setMessage('');
    } catch (error) {
      setMessage(cleanMessage(error instanceof Error ? error.message : 'Automations could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selected = bot?.automation.keywords.find((item) => item.keyword === selectedKeyword) ?? null;
  useEffect(() => {
    if (!selected) return;
    setKeywordResponse(selected.response);
    setKeywordCooldown(String(selected.cooldownSeconds));
  }, [selected?.keyword]);

  const visibleKeywords = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('en-GB');
    return (bot?.automation.keywords ?? []).filter((item) => !query || `${item.keyword} ${item.response}`.toLocaleLowerCase('en-GB').includes(query));
  }, [bot, search]);

  async function apply(operation: () => Promise<TwitchBotSnapshot>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      setBot(await operation());
      setMessage(success);
    } catch (error) {
      setMessage(cleanMessage(error instanceof Error ? error.message : 'Automation action failed.'));
    } finally {
      setBusy(false);
    }
  }

  async function toggleGroup(kind: 'keywords' | 'greetings' | 'notices', enabled: boolean) {
    const changes = kind === 'keywords' ? { keywordsEnabled: enabled } : kind === 'greetings' ? { greetingsEnabled: enabled } : { noticesEnabled: enabled };
    await apply(() => updateAutomationSettings(changes), `${kind === 'keywords' ? 'Keyword' : kind === 'greetings' ? 'Greeting' : 'Notice'} automations ${enabled ? 'enabled' : 'disabled'}.`);
  }

  async function saveKeyword() {
    if (!selected) return;
    const cooldown = validCooldown(keywordCooldown);
    if (cooldown === null) {
      setMessage('Cooldown must be a whole number from 0 to 3600 seconds.');
      return;
    }
    await apply(() => updateBotKeyword(selected.keyword, { response: keywordResponse, cooldownSeconds: cooldown }), `“${selected.keyword}” saved.`);
  }

  async function toggleKeyword(keyword: BotKeyword) {
    await apply(() => updateBotKeyword(keyword.keyword, { enabled: !keyword.enabled }), `“${keyword.keyword}” ${keyword.enabled ? 'disabled' : 'enabled'}.`);
  }

  if (loading && !bot) return <div className="ffs-next-loading">Loading Automations…</div>;

  const settings = bot?.automation.settings;
  const keywordCooldownValue = validCooldown(keywordCooldown);

  return (
    <div className="ffs-workspace-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}
      {bot ? (
        <>
          <section className="ffs-workspace-hero">
            <div><small>CHAT AUTOMATION</small><h2>Automations</h2><p>Control event-driven chat behaviour by what triggers it, not by where it originally came from.</p></div>
            <button type="button" className="ffs-button secondary" onClick={() => void refresh()} disabled={busy}><RefreshCw size={15} /> Refresh</button>
          </section>

          <section className="ffs-automation-switches">
            <AutomationSwitch icon={<WandSparkles size={18} />} title="Keywords" description={`${bot.automation.keywords.length.toLocaleString('en-GB')} keyword triggers`} enabled={settings?.keywords_enabled === true} disabled={busy} onChange={(enabled) => void toggleGroup('keywords', enabled)} />
            <AutomationSwitch icon={<UserRoundCheck size={18} />} title="Greetings" description={`${bot.automation.greetings.length.toLocaleString('en-GB')} personalised greetings`} enabled={settings?.greetings_enabled === true} disabled={busy} onChange={(enabled) => void toggleGroup('greetings', enabled)} />
            <AutomationSwitch icon={<Bell size={18} />} title="Notices" description={`${bot.automation.notices.length.toLocaleString('en-GB')} scheduled notice groups`} enabled={settings?.notices_enabled === true} disabled={busy} onChange={(enabled) => void toggleGroup('notices', enabled)} />
          </section>

          <section className="ffs-command-workspace">
            <article className="ffs-workspace-card ffs-command-list-card">
              <header><div><small>TRIGGERS</small><h3>Keyword automations</h3></div><span>{visibleKeywords.length.toLocaleString('en-GB')}</span></header>
              <label className="ffs-search-field"><Search size={15} /><input value={search} placeholder="Search keyword triggers" onChange={(event) => setSearch(event.target.value)} /></label>
              <div className="ffs-command-list">
                {visibleKeywords.map((keyword) => (
                  <button type="button" key={keyword.keyword} className={keyword.keyword === selectedKeyword ? 'selected' : ''} onClick={() => setSelectedKeyword(keyword.keyword)}>
                    <div><strong>{keyword.keyword}</strong><span>{keyword.isRegex ? 'Pattern match' : keyword.caseSensitive ? 'Exact case' : 'Text match'} · {keyword.cooldownSeconds}s cooldown</span></div>
                    <small className={keyword.enabled ? 'good' : 'muted'}>{keyword.enabled ? 'ON' : 'OFF'}</small>
                  </button>
                ))}
                {visibleKeywords.length === 0 ? <div className="ffs-empty-state">No keyword automations match this search.</div> : null}
              </div>
            </article>

            <article className="ffs-workspace-card ffs-command-editor-card">
              {selected ? (
                <>
                  <header><div><small>TRIGGER → ACTION</small><h3>{selected.keyword}</h3></div><strong className={selected.enabled ? 'ffs-status good' : 'ffs-status'}>{selected.enabled ? 'ENABLED' : 'DISABLED'}</strong></header>
                  <div className="ffs-command-editor">
                    <div className="ffs-trigger-summary"><span>When chat {selected.isRegex ? 'matches this pattern' : selected.caseSensitive ? 'matches this text with exact case' : 'contains this text'}</span><strong>{selected.keyword}</strong></div>
                    <label><span>Chat response <small>may be blank when a direct action is configured</small></span><textarea rows={4} maxLength={500} value={keywordResponse} onChange={(event) => setKeywordResponse(event.target.value)} /></label>
                    <label><span>Cooldown</span><div className="ffs-suffix-input"><input type="number" min="0" max="3600" value={keywordCooldown} aria-invalid={keywordCooldownValue === null} onChange={(event) => setKeywordCooldown(event.target.value)} /><b>seconds</b></div></label>
                    {selected.actions.length ? <div className="ffs-command-actions"><span>Direct actions</span>{selected.actions.map((action, index) => <div key={`${action.type}-${index}`}><CheckCircle2 size={14} /> {actionText(action)}</div>)}</div> : null}
                    <div className="ffs-inline-actions"><button type="button" className="ffs-button primary" disabled={busy || keywordCooldownValue === null} onClick={() => void saveKeyword()}>Save Changes</button><button type="button" className={`ffs-button ${selected.enabled ? 'warning' : 'secondary'}`} disabled={busy} onClick={() => void toggleKeyword(selected)}>{selected.enabled ? 'Disable Trigger' : 'Enable Trigger'}</button></div>
                  </div>
                </>
              ) : <div className="ffs-empty-state">Select a keyword automation to edit it.</div>}
            </article>
          </section>

          <section className="ffs-workspace-card">
            <header><div><small>SCHEDULED CHAT</small><h3>Notices</h3></div><MessageSquareText size={18} /></header>
            <div className="ffs-automation-cards">
              {bot.automation.notices.length === 0 ? <div className="ffs-empty-state">No scheduled notices are configured.</div> : bot.automation.notices.map((notice) => (
                <details className="ffs-automation-detail" key={notice.legacySlot}>
                  <summary><div><strong>{notice.name}</strong><span>{notice.enabled ? 'Enabled' : 'Disabled'} · every {notice.intervalMin}–{notice.intervalMax} min · after {notice.requiredMessages} chat messages</span></div><small>{notice.messages.length} message{notice.messages.length === 1 ? '' : 's'}</small></summary>
                  <NoticeEditor
                    notice={notice}
                    busy={busy}
                    onSave={(changes) => apply(
                      () => updateBotNotice(notice.legacySlot, changes),
                      `“${notice.name}” scheduled notice saved.`,
                    )}
                  />
                </details>
              ))}
            </div>
          </section>

          <section className="ffs-workspace-card">
            <header><div><small>PERSONALISED CHAT</small><h3>Greetings</h3></div><UserRoundCheck size={18} /></header>
            <div className="ffs-greeting-grid">
              {bot.automation.greetings.length === 0 ? <div className="ffs-empty-state">No personalised greetings are configured.</div> : bot.automation.greetings.map((greeting) => (
                <div className="ffs-greeting-card" key={greeting.userLogin}><strong>{greeting.userLogin}</strong><span>{greeting.message}</span><small>{greeting.enabled ? 'Enabled' : 'Disabled'}</small></div>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

function AutomationSwitch(props: { icon: ReactNode; title: string; description: string; enabled: boolean; disabled: boolean; onChange: (enabled: boolean) => void }) {
  return (
    <article className={`ffs-automation-switch ${props.enabled ? 'enabled' : ''}`}>
      <div className="icon">{props.icon}</div><div><strong>{props.title}</strong><span>{props.description}</span></div>
      <button type="button" className={`ffs-button ${props.enabled ? 'warning' : 'primary'}`} disabled={props.disabled} onClick={() => props.onChange(!props.enabled)}>{props.enabled ? 'Turn Off' : 'Turn On'}</button>
    </article>
  );
}


function NoticeEditor(props: {
  notice: BotNotice;
  busy: boolean;
  onSave: (changes: Parameters<typeof updateBotNotice>[1]) => Promise<void>;
}) {
  const { notice } = props;
  const [name, setName] = useState(notice.name);
  const [intervalMin, setIntervalMin] = useState(String(notice.intervalMin));
  const [intervalMax, setIntervalMax] = useState(String(notice.intervalMax));
  const [requiredMessages, setRequiredMessages] = useState(String(notice.requiredMessages));
  const [offlineEnabled, setOfflineEnabled] = useState(notice.offlineEnabled);
  const [shuffle, setShuffle] = useState(notice.shuffle);
  const [messages, setMessages] = useState([...notice.messages]);
  const [disabledMessages, setDisabledMessages] = useState(
    notice.messages.map((_message, index) => notice.disabledMessages[index] === true),
  );
  const [error, setError] = useState('');

  useEffect(() => {
    setName(notice.name);
    setIntervalMin(String(notice.intervalMin));
    setIntervalMax(String(notice.intervalMax));
    setRequiredMessages(String(notice.requiredMessages));
    setOfflineEnabled(notice.offlineEnabled);
    setShuffle(notice.shuffle);
    setMessages([...notice.messages]);
    setDisabledMessages(notice.messages.map((_message, index) => notice.disabledMessages[index] === true));
    setError('');
  }, [notice]);

  function wholeNumber(value: string, minimum: number, maximum: number): number | null {
    if (!/^\d+$/.test(value.trim())) return null;
    const number = Number(value);
    return Number.isInteger(number) && number >= minimum && number <= maximum ? number : null;
  }

  async function save() {
    const minimum = wholeNumber(intervalMin, 1, 180);
    const maximum = wholeNumber(intervalMax, 1, 180);
    const required = wholeNumber(requiredMessages, 0, 1000);
    const cleanedMessages = messages.map((message) => message.trim());

    if (!name.trim() || name.trim().length > 80) { setError('Group name must be 1–80 characters.'); return; }
    if (minimum === null || maximum === null || maximum < minimum) {
      setError('Use a valid 1–180 minute range, with maximum greater than or equal to minimum.'); return;
    }
    if (required === null) { setError('Required chat messages must be a whole number from 0 to 1,000.'); return; }
    if (cleanedMessages.length > 20 || cleanedMessages.some((message) => !message || message.length > 500)) {
      setError('Messages must be non-empty, up to 500 characters each, with no more than 20 per group.'); return;
    }
    if (notice.enabled && (cleanedMessages.length === 0 || cleanedMessages.every((_message, index) => disabledMessages[index] === true))) {
      setError('Keep at least one message enabled while this notice group is enabled.'); return;
    }

    setError('');
    await props.onSave({
      name: name.trim(), intervalMin: minimum, intervalMax: maximum, requiredMessages: required,
      offlineEnabled, shuffle, messages: cleanedMessages,
      disabledMessages: cleanedMessages.map((_message, index) => disabledMessages[index] === true),
    });
  }

  function addMessage() {
    if (messages.length >= 20) return;
    setMessages((current) => [...current, '']);
    setDisabledMessages((current) => [...current, false]);
  }

  function removeMessage(index: number) {
    setMessages((current) => current.filter((_message, itemIndex) => itemIndex !== index));
    setDisabledMessages((current) => current.filter((_disabled, itemIndex) => itemIndex !== index));
  }

  return (
    <div className="ffs-notice-editor">
      <div className="ffs-notice-settings-grid">
        <label><span>Group name</span><input maxLength={80} value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label><span>Minimum interval</span><div className="ffs-suffix-input"><input type="number" min="1" max="180" value={intervalMin} onChange={(event) => setIntervalMin(event.target.value)} /><b>min</b></div></label>
        <label><span>Maximum interval</span><div className="ffs-suffix-input"><input type="number" min="1" max="180" value={intervalMax} onChange={(event) => setIntervalMax(event.target.value)} /><b>min</b></div></label>
        <label><span>Chat messages required</span><input type="number" min="0" max="1000" value={requiredMessages} onChange={(event) => setRequiredMessages(event.target.value)} /></label>
      </div>

      <div className="ffs-notice-toggles">
        <label><input type="checkbox" checked={shuffle} onChange={(event) => setShuffle(event.target.checked)} /> Shuffle messages</label>
        <label><input type="checkbox" checked={offlineEnabled} onChange={(event) => setOfflineEnabled(event.target.checked)} /> Allow while channel is offline</label>
      </div>

      <div className="ffs-notice-runtime">
        <span>Since last notice <strong>{notice.messagesSinceLast.toLocaleString('en-GB')} chat messages</strong></span>
        <span>Last sent <strong>{notice.lastSentAt ? new Date(notice.lastSentAt).toLocaleString('en-GB') : 'Never'}</strong></span>
        <span>Next due <strong>{notice.nextDueAt ? new Date(notice.nextDueAt).toLocaleString('en-GB') : notice.enabled ? 'Schedules after save/enable' : 'Disabled'}</strong></span>
      </div>

      <div className="ffs-notice-message-list">
        {messages.map((item, index) => (
          <div className="ffs-notice-message-row" key={`${notice.legacySlot}-message-${index}`}>
            <label className="ffs-notice-message-enabled">
              <input type="checkbox" checked={disabledMessages[index] !== true} onChange={(event) => {
                const enabled = event.target.checked;
                setDisabledMessages((current) => current.map((disabled, itemIndex) => itemIndex === index ? !enabled : disabled));
              }} />
              Use
            </label>
            <textarea rows={2} maxLength={500} value={item} onChange={(event) => {
              const value = event.target.value;
              setMessages((current) => current.map((message, itemIndex) => itemIndex === index ? value : message));
            }} />
            <button type="button" className="ffs-button secondary" disabled={props.busy} onClick={() => removeMessage(index)}>Remove</button>
          </div>
        ))}
        {messages.length === 0 ? <div className="ffs-empty-state">This group has no messages yet.</div> : null}
      </div>

      {error ? <div className="ffs-workspace-message">{error}</div> : null}
      <div className="ffs-inline-actions">
        <button type="button" className="ffs-button secondary" disabled={props.busy || messages.length >= 20} onClick={addMessage}>Add Message</button>
        <button type="button" className="ffs-button primary" disabled={props.busy} onClick={() => void save()}>Save Notice</button>
        <button type="button" className={`ffs-button ${notice.enabled ? 'warning' : 'primary'}`} disabled={props.busy} onClick={() => void props.onSave({ enabled: !notice.enabled })}>
          {notice.enabled ? 'Disable Group' : 'Enable Group'}
        </button>
      </div>
    </div>
  );
}
